import { useState, useCallback, useEffect } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { parseM3U } from '../utils/m3uParser';
import { apiUrl } from '../utils/apiBase';
const API_BASE = apiUrl('/api/favorites');

export const useFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const authenticatedFetch = useAuthenticatedFetch();

  const loadFavorites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedFetch(API_BASE);
      if (!res.ok) throw new Error('Errore nel caricamento preferiti');
      const data = await res.json();
      setFavorites(parseM3U(data.content || ''));
    } catch (err) {
      setError(err.message);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const addFavorite = useCallback(async (channel) => {
    try {
      await authenticatedFetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(channel)
      });
      await loadFavorites();
    } catch (err) {
      setError(err.message);
    }
  }, [authenticatedFetch, loadFavorites]);

  const removeFavorite = useCallback(async (url) => {
    try {
      await authenticatedFetch(`${API_BASE}?url=${encodeURIComponent(url)}`, { method: 'DELETE' });
      await loadFavorites();
    } catch (err) {
      setError(err.message);
    }
  }, [authenticatedFetch, loadFavorites]);

  const toggleFavorite = useCallback((channel) => {
    const exists = favorites.some(f => f.url === channel.url);
    if (exists) {
      return removeFavorite(channel.url);
    }
    return addFavorite(channel);
  }, [favorites, addFavorite, removeFavorite]);

  const isFavorite = useCallback((channel) => favorites.some(f => f.url === channel.url), [favorites]);

  useEffect(() => { loadFavorites(); }, []);

  return { favorites, loading, error, loadFavorites, toggleFavorite, isFavorite };
};
